import {
  all,
  call,
  cancel,
  fork,
  put,
  select,
  takeLatest,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';

import api from 'api';

import ordersActions from 'state/orders/actionCreators';
import ordersSelectors from 'state/orders/selectors';
import { constants as recurringOrderConstants } from 'utils/recurringOrder';
import {
  actions as analyticsActions,
  constants as analyticsEvents,
} from 'state/analytics';
import { actions as uiActions } from 'state/ui';

import routes from 'utils/routes';
import { isEmpty } from 'utils/types';

const sortOrdersByDate = {
  asc: (orderA, orderB) =>
    orderA.start_time_from <= orderB.start_time_from ? -1 : 1,
  desc: (orderA, orderB) =>
    orderA.start_time_from >= orderB.start_time_from ? -1 : 1,
};

const baseFilters = {
  active: 'asc',
  previous: 'desc',
};

let shipperOrdersPollingTask;

const ORDERING_FIELD = 'calculated_fields__pickup_time_from';

const baseParams = {
  active: [
    {
      ordering: `${ORDERING_FIELD}`,
      last_dropoff_time_gte: moment().format('YYYY-MM-DD'),
      page_size: 10,
    },
  ],
  previous: [
    {
      ordering: `-${ORDERING_FIELD}`,
      last_dropoff_time: moment().subtract(1, 'day').format('YYYY-MM-DD'),
      page_size: 10,
    },
  ],
  recurring: [
    {
      status: ['ACTIVE', 'CANCELLED'],
      ordering: 'start_from',
      end_after: moment().subtract(1, 'month').format('YYYY-MM-DD'),
      page_size: 10,
    },
  ],
  search: [
    {
      ordering: `-${ORDERING_FIELD}`,
      page_size: 10,
    },
  ],
};

const fetchOrdersWithFilter = async (params, type) => {
  const endpoint =
    type === recurringOrderConstants.RECURRING
      ? api.getRecurringOrders
      : api.getOrders;
  return await endpoint({ params });
};

const fetchOrdersFromPages = async (url) => api.authGet(url);

const mergeResponses = (responses) =>
  responses.reduce((acc, response) => {
    const { data } = response;
    const results = data && data.results ? data.results : data;

    return [...(acc || []), ...(results || [])];
  }, []);

const mergePages = (responses) =>
  responses.reduce((acc, response) => {
    const { data } = response;
    return data && data.next ? [...acc, data.next] : [...acc];
  }, []);

const mergeCount = (responses) =>
  responses.reduce((acc, response) => {
    const { data } = response;
    return acc + (data?.count || 0);
  }, 0);

function* fetchOrders({ payload }) {
  const { type, isPolling } = payload;
  const text = yield select(ordersSelectors.getSearchText);
  const userId = yield select(ordersSelectors.getFilterUserId);
  const firstPickupTime = yield select(
    ordersSelectors.getFilterFirstPickupTime
  );
  const lastDropoffTime = yield select(
    ordersSelectors.getFilterLastDropoffTime
  );

  const pages = yield select(ordersSelectors.getPages);
  if (!isPolling) {
    yield put(ordersActions.setFetching(true));
  }

  try {
    const firstPageResp = yield all(
      baseParams[type].map((params) =>
        call(
          fetchOrdersWithFilter,
          {
            ...params,
            page: 1,
            ...(isEmpty(text) ? {} : { search: text }),
            ...(userId ? { user: userId } : {}),
            ...(firstPickupTime ? { first_pickup_time: firstPickupTime } : {}),
            ...(lastDropoffTime ? { last_dropoff_time: lastDropoffTime } : {}),
          },
          type
        )
      )
    );
    const pagesResp = yield all(
      pages.map((url) => call(fetchOrdersFromPages, url))
    );
    const response = [...firstPageResp, ...pagesResp];
    const dataMerged = yield call(mergeResponses, response);
    const pagesMerged = yield call(mergePages, response);
    const countMerged = yield call(mergeCount, response);

    const normalizedOrders = (dataMerged || []).reduce(
      (obj, order) => ({
        ...obj,
        [order.id || order.recurring_order_id]: { ...order },
      }),
      {}
    );

    const orderIdsSorted = dataMerged
      .sort(sortOrdersByDate[baseFilters[type]])
      .map((order) => order.id);

    yield put(ordersActions.setFetching(false));

    return {
      normalizedOrders,
      orderIdsSorted,
      pages: pagesMerged,
      count: countMerged,
    };
  } catch (err) {
    yield console.error('ERROR', err);
  }
  yield put(ordersActions.setFetching(false));
  return false;
}

function* handleFetchOrdersPolling({ payload }) {
  try {
    while (true) {
      yield call(delay, 60000);
      const data = yield call(fetchOrders, {
        payload: { ...payload, isPolling: true },
      });
      if (data) {
        yield put(ordersActions.setData({ data }));
      }
    }
  } catch (e) {
    console.error(e);
  }
}

function* handleFetchOrdersStartPolling({ payload }) {
  shipperOrdersPollingTask = yield fork(handleFetchOrdersPolling, {
    payload,
  });
}

function* handleFetchOrdersStopPolling() {
  if (shipperOrdersPollingTask) yield cancel(shipperOrdersPollingTask);
}

function* handleFetchOrders({ payload }) {
  const data = yield call(fetchOrders, { payload });

  if (data) {
    yield put(ordersActions.setData({ data }));
  }
}

function* handleFetchMoreOrders({ payload }) {
  const data = yield call(fetchOrders, { payload });

  if (data) {
    yield put(ordersActions.addData({ data }));
  }
}

function* handleOpenOrder({ payload }) {
  const { orderId, type } = payload;

  yield put(push(`${routes.orders[type]}/${orderId}`));
}

function* handleCloseOrder({ payload }) {
  const { type } = payload;
  yield put(push(`${routes.orders[type]}`));
  const numberOfOrders = yield select(ordersSelectors.getNumberOfOrdersLoaded);
  if (!numberOfOrders) {
    yield put(ordersActions.fetchOrders({ type }));
  }
}

const getTypeFromPathName = (pathname) => {
  if (pathname === routes.orders.active || pathname === 'active')
    return 'active';
  if (pathname === routes.orders.previous || pathname === 'previous')
    return 'previous';
  if (pathname === routes.orders.recurring || pathname === 'recurring')
    return 'recurring';
  return 'active';
};

function* handleSearchByText({ payload }) {
  const { text, pathname } = payload;
  yield put(ordersActions.resetData());
  yield put(ordersActions.setSearchText(text));

  const data = yield call(fetchOrders, {
    payload: { type: text ? 'search' : getTypeFromPathName(pathname) },
  });

  if (data) {
    yield put(ordersActions.setData({ data }));
  }
}

function* cleanFilters() {
  const firstPickupTime = yield select(
    ordersSelectors.getFilterFirstPickupTime
  );
  const lastDropoffTime = yield select(
    ordersSelectors.getFilterLastDropoffTime
  );
  const user = yield select(ordersSelectors.getFilterUser);

  yield put(ordersActions.resetData());

  if (!isEmpty(firstPickupTime)) {
    yield put(
      ordersActions.setDate({
        type: 'first_pickup_time',
        value: firstPickupTime,
      })
    );
  }

  if (!isEmpty(lastDropoffTime)) {
    yield put(
      ordersActions.setDate({
        type: 'last_dropoff_time',
        value: lastDropoffTime,
      })
    );
  }

  if (!isEmpty(user)) {
    yield put(ordersActions.setUser(user));
  }
}

function* handleSearchByUser({ payload }) {
  const { user, pathname } = payload;

  yield call(cleanFilters);
  if (isEmpty(user)) {
    yield put(ordersActions.clearUser());
  } else {
    yield put(ordersActions.setUser(user));
  }

  const data = yield call(fetchOrders, {
    payload: { type: getTypeFromPathName(pathname) },
  });

  if (data) {
    yield put(ordersActions.setData({ data }));
  }
}

function* handleSearchByDate({ payload }) {
  const { type, value, pathname } = payload;

  yield call(cleanFilters);

  if (!isEmpty(type) && !isEmpty(value)) {
    yield put(ordersActions.setDate({ type, value }));
  } else if (!isEmpty(type)) {
    yield put(ordersActions.clearDate({ type }));
  } else {
    yield put(ordersActions.clearDate({ type: 'first_pickup_time' }));
    yield put(ordersActions.clearDate({ type: 'last_dropoff_time' }));
  }

  const data = yield call(fetchOrders, {
    payload: { type: getTypeFromPathName(pathname) },
  });

  if (data) {
    yield put(ordersActions.setData({ data }));
  }
}

function* handleCancelOrder({ payload }) {
  const { order, reason, comment } = payload;

  try {
    yield put(
      analyticsActions.sendEvent(
        analyticsEvents.shipperOrderCancelSubmitted({
          orderId: order.id,
          reason,
        })
      )
    );

    yield call(api.put, `/shipper-app/shipper_orders/${order.id}/cancel/`, {
      action: 'CANCEL_SHIPPER_ORDER',
      reason,
      comment: reason === 'REASON_OTHER' ? comment : '',
    });

    yield put(uiActions.setFeedback({ feedback: 'Orders:cancel.cancelled' }));
    yield put(uiActions.toggleCancelOrderModalOpened({ order: false }));
    yield call(delay, 10000);
  } catch (err) {
    yield put(
      uiActions.setFeedback({
        feedback: 'Orders:cancel.cancelled-error',
        success: false,
      })
    );
    yield put(uiActions.toggleCancelOrderModalOpened({ order: false }));
    yield call(delay, 50000);
  } finally {
    yield put(uiActions.setFeedback({ feedback: null }));
  }
}

export default function* () {
  yield takeLatest(ordersActions.fetchOrders, handleFetchOrders);
  yield takeLatest(
    ordersActions.fetchOrdersStartPolling,
    handleFetchOrdersStartPolling
  );
  yield takeLatest(
    ordersActions.fetchOrdersStopPolling,
    handleFetchOrdersStopPolling
  );
  yield takeLatest(ordersActions.fetchMoreOrders, handleFetchMoreOrders);

  yield takeLatest(ordersActions.openOrder, handleOpenOrder);
  yield takeLatest(ordersActions.closeOrder, handleCloseOrder);

  yield takeLatest(ordersActions.searchByText, handleSearchByText);

  yield takeLatest(ordersActions.setUserFilter, handleSearchByUser);
  yield takeLatest(ordersActions.clearUserFilter, handleSearchByUser);

  yield takeLatest(ordersActions.setDateFilter, handleSearchByDate);
  yield takeLatest(ordersActions.clearDateFilter, handleSearchByDate);
  yield takeLatest(ordersActions.clearDatesFilter, handleSearchByDate);

  yield takeLatest(ordersActions.cancelOrder, handleCancelOrder);
}

export {
  handleFetchOrdersStartPolling,
  handleFetchOrdersStopPolling,
  handleFetchOrdersPolling,
  fetchOrders,
  shipperOrdersPollingTask,
};
